import { inject, Injectable } from '@angular/core';

import { ApiError } from './api-error';

import { I18nService } from '@core/i18n/i18n.service';
import type { TranslationKey } from '@core/i18n/translations/en';

export interface ErrorMessageKeys {
  readonly title?: TranslationKey;
  readonly message: TranslationKey;
}

export interface ErrorMessageCopy {
  readonly title?: string;
  readonly message: string;
}

export const CODE_MESSAGES: Readonly<Record<string, ErrorMessageKeys>> = {
  ERROR_AUTH_USER_NOT_ACTIVE: {
    title: 'error.user.userNotActive.title',
    message: 'error.user.userNotActive.message',
  },
  ERROR_USER_IS_NOT_ACTIVE: {
    title: 'error.user.userNotActive.title',
    message: 'error.user.userNotActive.message',
  },
  ERROR_USERNAME_INVALID: {
    message: 'error.user.usernameInvalid',
  },
  ERROR_LINK_EXPIRED: { message: 'reset.expired' },
  ERROR_GENERIC_ERROR: {
    message: 'error.generic.message',
  },
  ERROR_FAILED_TO_AUTHENTICATE: {
    message: 'error.auth.failedToLogin',
  },
  ERROR_USER_NOT_ACTIVE: {
    message: 'error.auth.userInactive',
  },
  ERROR_INVALID_CREDENTIALS: {
    message: 'error.auth.emailOrPasswordInvalid',
  },
  ERROR_PASSWORDS_DO_NOT_MATCH: {
    message: 'error.auth.passwordsDoNotMatch',
  },
  ERROR_USER_ALREADY_EXISTS: {
    message: 'error.auth.userAlreadyExists',
  },
  // Email confirmation — a correct password on an unconfirmed account, and a
  // confirmation link that is too old (410) or unknown (404).
  ERROR_EMAIL_NOT_CONFIRMED: { message: 'error.auth.emailNotConfirmed' },
  AUTH_CONFIRMATION_EXPIRED: { message: 'error.auth.confirmationExpired' },
  AUTH_CONFIRMATION_INVALID: { message: 'error.auth.confirmationInvalid' },
  ERROR_FAILED_TO_DECODE_TOKEN: {
    message: 'error.auth.failedToDecodeToken',
  },
  ERROR_FAILED_TO_SAVE_TOKEN: {
    message: 'error.auth.failedToSaveToken',
  },
  ERROR_FAILED_TO_SET_WORKSPACE: {
    message: 'error.company.failedToChange',
  },
  ERROR_FAILED_TO_SET_DEFAULT_WORKSPACE: {
    message: 'error.workspace.failedToSetDefault',
  },
  ERROR_COMPANY_NAME_TOO_LONG: {
    message: 'error.company.nameTooLong',
  },
  ERROR_COMPANY_PROVINCE_INVALID: { message: 'error.company.provinceInvalid' },
  // PUT /workspaces/{id} — see internal/enterprise/delivery/http/hq.go.
  ERROR_COMPANY_CODE_INVALID: { message: 'error.company.codeInvalid' },
  ERROR_COMPANY_EMAIL_INVALID: { message: 'error.company.emailInvalid' },
  ERROR_COMPANY_COUNTRY_INVALID: { message: 'error.company.countryInvalid' },
  ERROR_CAN_NOT_DELETE_LAST_RECORD: {
    message: 'error.record.cannotDeleteLast',
  },
  // clrTaskerREST — see internal/tasker/delivery/http/http.go.
  TASKER_NO_ACTIVE_WORKSPACE: { message: 'error.tasker.noWorkspace' },
  TASKER_FORBIDDEN: { message: 'error.tasker.forbidden' },
  TASKER_NOT_FOUND: { message: 'error.tasker.notFound' },
  TASKER_SELF_APPROVAL: { message: 'error.tasker.selfApproval' },
  TASKER_INVALID_TRANSITION: { message: 'error.tasker.invalidTransition' },
  TASKER_ACTIVE_VERSION_EXISTS: { message: 'error.tasker.activeVersionExists' },
  TASKER_NO_ACTIVE_VERSION: { message: 'error.tasker.noActiveVersion' },
  TASKER_TASK_CLOSED: { message: 'error.tasker.taskClosed' },
  TASKER_COMMENT_REQUIRED: { message: 'error.tasker.commentRequired' },
  TASKER_NOT_ASSIGNEE: { message: 'error.tasker.notAssignee' },
  TASKER_ELEMENT_IN_USE: { message: 'error.tasker.elementInUse' },
  TASKER_CONCURRENT_UPDATE: { message: 'error.tasker.concurrentUpdate' },
  TASKER_VALIDATION_FAILED: { message: 'error.tasker.validation' },
  TASKER_UNAVAILABLE: { message: 'error.tasker.unavailable' },
  // Learner — see internal/learner/delivery/http/http.go.
  LEARNER_NO_ACTIVE_WORKSPACE: { message: 'error.learner.noWorkspace' },
  LEARNER_FORBIDDEN: { message: 'error.learner.forbidden' },
  LEARNER_NOT_FOUND: { message: 'error.learner.notFound' },
  LEARNER_VALIDATION_FAILED: { message: 'error.learner.validation' },
  LEARNER_DUPLICATE_TITLE: { message: 'error.learner.duplicateTitle' },
  LEARNER_INVALID_STATE: { message: 'error.learner.invalidState' },
  LEARNER_SELF_REVIEW: { message: 'error.learner.selfReview' },
  LEARNER_CONCURRENT_UPDATE: { message: 'error.learner.concurrentUpdate' },
  LEARNER_UNAVAILABLE: { message: 'error.learner.unavailable' },
  // App market gate — see pkg/http/middleware/apps.go.
  APP_NOT_ENABLED: { message: 'error.app.notEnabled' },
  APP_NOT_PAID: { message: 'error.app.notPaid' },
  // EML wallet — see internal/eml/delivery/http/hq.go.
  EML_NO_ACTIVE_WORKSPACE: { message: 'error.eml.noWorkspace' },
  EML_WALLET_CREATING: { message: 'error.eml.creating' },
  EML_WALLET_NOT_FOUND: { message: 'error.eml.notFound' },
  EML_UNAVAILABLE: { message: 'error.eml.unavailable' },
  EML_NOT_CONFIGURED: { message: 'error.eml.notConfigured' },
  EML_INVALID_AMOUNT: { message: 'error.eml.invalidAmount' },
  EML_INVALID_REQUEST: { message: 'error.eml.invalidRequest' },
  EML_PAYMENT_NOT_FOUND: { message: 'error.eml.paymentNotFound' },
  EML_NOT_PAYABLE: { message: 'error.eml.notPayable' },
  EML_PAYEE_HAS_NO_WALLET: { message: 'error.eml.payeeHasNoWallet' },
  EML_CURRENCY_MISMATCH: { message: 'error.eml.currencyMismatch' },
  EML_WALLET_INACTIVE: { message: 'error.eml.walletInactive' },
  EML_ADDRESS_INCOMPLETE: { message: 'error.eml.addressIncomplete' },
  EML_CARDHOLDER_INCOMPLETE: { message: 'error.eml.cardholderIncomplete' },
  EML_REGISTRATION_REFUSED: { message: 'error.eml.registrationRefused' },
  EML_INSUFFICIENT_FUNDS: { message: 'error.eml.insufficientFunds' },
  EML_MOVEMENT_IN_PROGRESS: { message: 'error.eml.movementInProgress' },
  EML_MOVEMENT_NEEDS_REVIEW: { message: 'error.eml.needsReview' },
  EML_CREDIT_PENDING: { message: 'error.eml.creditPending' },
  EML_REFUSED: { message: 'error.eml.refused' },
  // Payments module (clrPaymentPropsREST) — refusals before EML is asked.
  PAYMENTS_FORBIDDEN: { message: 'error.payments.forbidden' },
  PAYMENTS_NO_ACTIVE_WORKSPACE: { message: 'error.eml.noWorkspace' },
  PAYMENTS_INVALID_REQUEST: { message: 'error.eml.invalidRequest' },
  PAYMENTS_NOT_CONFIGURED: { message: 'error.eml.notConfigured' },
  PAYMENTS_UNAVAILABLE: { message: 'error.eml.unavailable' },
  PAYMENTS_INVALID_AMOUNT: { message: 'error.payments.invalidAmount' },
  PAYMENTS_INVALID_DESCRIPTION: { message: 'error.payments.invalidDescription' },
  PAYMENTS_INVALID_DATE: { message: 'error.payments.invalidDate' },
  PAYMENTS_EMPLOYEE_NOT_FOUND: { message: 'error.payments.employeeNotFound' },
  PAYMENTS_REQUEST_NOT_FOUND: { message: 'error.payments.requestNotFound' },
  PAYMENTS_REQUEST_NOT_CANCELLABLE: { message: 'error.payments.requestNotCancellable' },
  // Payroll by Payout Partner — see internal/eor/domain/model.go.
  EOR_FORBIDDEN: { message: 'error.payroll.forbidden' },
  EOR_NO_ACTIVE_WORKSPACE: { message: 'error.payroll.noWorkspace' },
  EOR_NOT_CONFIGURED: { message: 'error.payroll.notConfigured' },
  EOR_PROVIDER_UNAVAILABLE: { message: 'error.payroll.unavailable' },
  EOR_UNAVAILABLE: { message: 'error.payroll.unavailable' },
  EOR_PROVIDER_REFUSED: { message: 'error.payroll.refused' },
  EOR_INVALID_REQUEST: { message: 'error.payroll.invalid' },
  EOR_NOT_FOUND: { message: 'error.payroll.notFound' },
  EOR_NOTHING_TO_PAY: { message: 'error.payroll.nothingToPay' },
  EOR_END_CLIENT_REQUIRED: { message: 'error.payroll.clientRequired' },
  EOR_RECORD_TAKEN: { message: 'error.payroll.recordTaken' },
  EOR_NOT_ENROLLED: { message: 'error.payroll.notEnrolled' },
  // Hours worked in a volunteer's (unpaid) position — internal/positions.
  PAYMENT_UNPAID_POSITION: { message: 'error.payroll.unpaidPosition' },
  APP_NOT_IN_COUNTRY: { message: 'error.app.notInCountry' },
  // Workspace projects — see internal/projects/delivery/http/http.go.
  PROJECT_NO_ACTIVE_WORKSPACE: { message: 'error.projects.noWorkspace' },
  PROJECT_NOT_FOUND: { message: 'error.projects.notFound' },
  PROJECT_INVALID_REQUEST: { message: 'error.projects.invalid' },
  PROJECT_UNAVAILABLE: { message: 'error.projects.unavailable' },
  // A `/projects/{id}/scheduler/…` call for an id outside the workspace.
  SCHEDULER_PROJECT_NOT_FOUND: { message: 'error.projects.notFound' },
  // PUT /scheduler/shifts/force/{action} — check-in and the other time-clock actions.
  ERROR_CANNOT_TIMETRACK_FOR_DRAFT: { message: 'error.scheduler.cannotTimeTrackDraft' },
  // Communications engine — see internal/communications/engine/admin.go.
  COMMS_FORBIDDEN: { message: 'error.comms.forbidden' },
  COMMS_NO_ACTIVE_WORKSPACE: { message: 'error.comms.noWorkspace' },
  COMMS_NOT_FOUND: { message: 'error.comms.notFound' },
  COMMS_INVALID_REQUEST: { message: 'error.comms.invalid' },
  COMMS_BAD_REQUEST: { message: 'error.comms.badRequest' },
  COMMS_RETRY_UNAVAILABLE: { message: 'error.comms.retryUnavailable' },
  COMMS_NOT_FAILED: { message: 'error.comms.notFailed' },
  COMMS_UNAVAILABLE: { message: 'error.comms.unavailable' },
  // Positions — see internal/employees/delivery/http/positions.go.
  POSITION_NAME_TAKEN: { message: 'users.positions.nameTaken' },
  POSITION_NOT_FOUND: { message: 'users.positions.notFound' },
  INVALID_POSITION: { message: 'users.positions.invalid' },
  // Recruiting — see internal/recruiting/delivery/http/http.go.
  RECRUITING_NOT_FOUND: { message: 'error.recruiting.notFound' },
  RECRUITING_NOT_OPEN: { message: 'error.recruiting.notOpen' },
  RECRUITING_ALREADY_MEMBER: { message: 'error.recruiting.alreadyMember' },
  RECRUITING_TRANSITION: { message: 'error.recruiting.transition' },
  RECRUITING_INVALID: { message: 'error.recruiting.invalid' },
  RECRUITING_FORBIDDEN: { message: 'error.recruiting.forbidden' },
  RECRUITING_ACCOUNT_EXISTS: { message: 'error.recruiting.accountExists' },
  RECRUITING_NOT_SENT: { message: 'error.recruiting.notSent' },
};

@Injectable({ providedIn: 'root' })
export class ErrorMessageService {
  private readonly t = inject(I18nService).t;

  private readonly defaultErrorMessage: ErrorMessageCopy = {
    title: undefined,
    message: this.t('error.generic.message'),
  };

  private entryFor(code: string): ErrorMessageCopy {
    let entry: ErrorMessageKeys = CODE_MESSAGES['ERROR_GENERIC_ERROR']!;

    if (code in CODE_MESSAGES) {
      entry = CODE_MESSAGES[code]!;
    }

    return {
      title: entry.title === undefined ? undefined : this.t(entry.title),
      message: this.t(entry.message),
    };
  }

  toUserMessage(error: unknown): string {
    if (!ApiError.isApiError(error)) {
      return this.defaultErrorMessage.message;
    }

    const byCode = error.code === null ? undefined : this.entryFor(error.code);

    return byCode?.message ?? this.defaultErrorMessage.message;
  }
}
